import { Alert, AlertTitle, Button, Paper, Typography } from "@mui/material";
import { buildPermission } from "portal/utils/auth";
import { capitalize, titleCase } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import {
  DEFAULT_REPORT,
  formatWeeklyDay,
  formatWeeklyHour,
  isNewReport,
  normalizeVisibleColumns,
  secondsToRange,
} from "portal/utils/reports";
import { FeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import {
  getReportInstancesPath,
  getReportPath,
  Path,
  Slug,
} from "portal/utils/routing";
import { Header } from "portal/components/header/Header";
import { ImplementationStatus } from "portal/utils/robots";
import { isEqual } from "portal/utils/equality";
import { isUndefined } from "portal/utils/identity";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loading } from "portal/components/Loading";
import { NoScroll, Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { ReportEditor } from "portal/components/reports/ReportEditor";
import { ReportResponse } from "protos/portal/reports";
import { ReportTable } from "portal/components/reports/ReportTable";
import { ReportTools } from "portal/components/reports/ReportTools";
import { RunReportDialog } from "portal/components/reports/RunReportDialog";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withFeatureFlag } from "portal/components/RequireFeatureFlag";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/RestartAlt";

const _Report: FunctionComponent = () => {
  const { report: slug } = useParams();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { user } = useSelf();
  const canUpdateAll = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.all
    )
  );
  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  /**
   * Report
   */
  const { data, isLoading, isSuccess } = useQueryPopups(
    portalApi.endpoints.getReport.useQuery(
      slug ? { reportSlug: slug } : skipToken,
      {
        skip: slug === Slug.NEW,
      }
    )
  );

  const [createReport] = useMutationPopups(
    portalApi.endpoints.createReport.useMutation(),
    {
      success: capitalize(
        t("utils.actions.createdLong", {
          subject: capitalize(t("models.reports.report_one")),
        })
      ),
    }
  );

  const [updateReport] = useMutationPopups(
    portalApi.endpoints.updateReport.useMutation(),
    {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: capitalize(t("models.reports.report_one")),
        })
      ),
    }
  );
  const report = useMemo<ReportResponse | undefined>(() => {
    if (!user) {
      return;
    }
    if (slug === Slug.NEW) {
      return structuredClone(DEFAULT_REPORT);
    } else if (data) {
      return data;
    }
  }, [data, slug, user]);

  const reportExists = isNewReport(report) || (isSuccess && Boolean(report));

  // get all robots
  const useJigsaw = useJigsawMetrics();
  const { data: summaries } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery(
      report?.automateWeekly ? {} : skipToken
    )
  );
  const activeRobotIds = useMemo<number[] | undefined>(() => {
    if (report?.automateWeekly && summaries) {
      return summaries
        .filter(
          (summary) =>
            summary.robot?.implementationStatus ===
              ImplementationStatus.ACTIVE &&
            summary.customer?.db?.id === report.customerId
        )
        .map((summary) => summary.robot?.db?.id)
        .filter((id) => !isUndefined(id));
    }
  }, [report?.automateWeekly, report?.customerId, summaries]);

  // keep track of editing state
  const [isEditing, setEditing] = useState<boolean>(slug === Slug.NEW);
  // local version of report for editing
  const [localReport, setLocalReport] = useState<ReportResponse | undefined>();
  // when editing, only fetch report data when user clicks Preview (avoids hammering BE on every edit)
  const [previewReport, setPreviewReport] = useState<
    ReportResponse | undefined
  >(undefined);
  useEffect(() => {
    // Normalize up front so editing, reordering, and saving all operate on
    // column ids consistent with the current jigsaw flag state.
    setLocalReport(
      report && {
        ...report,
        visibleColumns: normalizeVisibleColumns(
          report.visibleColumns,
          useJigsaw
        ),
      }
    );
  }, [report, useJigsaw]);
  useEffect(() => {
    if (!isEditing) {
      setPreviewReport(undefined);
    }
  }, [isEditing]);

  // run report
  const [isRunning, setRunning] = useState<boolean>(false);
  let viewingReport = isEditing ? localReport : report;
  if (viewingReport?.automateWeekly) {
    const now = DateTime.local();
    viewingReport = {
      ...viewingReport,
      robotIds: activeRobotIds ?? [],
      startDate: now
        .plus({
          days: -(report?.customer?.weeklyReportLookbackDays ?? 14),
        })
        .toUnixInteger(),
      endDate: now.toUnixInteger(),
    };
  }

  if (!localReport) {
    return <Loading />;
  }

  return (
    <>
      <Header
        title={titleCase(t("models.reports.report_other"))}
        parentLink={Path.REPORTS}
      >
        <div className="flex-grow"></div>
        {(isLoading || reportExists) && (
          <div className="flex items-center gap-2">
            <ReportTools
              className={classes("hidden", { "md:flex": !isEditing })}
              readOnly
              dateRange={secondsToRange(
                viewingReport?.startDate,
                viewingReport?.endDate
              )}
              selectedRobots={viewingReport?.robotIds}
            />
            {!isEditing && (
              <>
                <Button
                  variant="text"
                  className="my-4 md:hidden"
                  component={Link}
                  to={getReportInstancesPath(slug)}
                >
                  {t("models.reportInstances.run_other")}
                </Button>
                <Button
                  variant="outlined"
                  className={"md:hidden"}
                  onClick={() => setEditing(true)}
                >
                  {t("utils.actions.edit")}
                </Button>
              </>
            )}
            {!isNewReport(report) && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setRunning(true)}
                disabled={isEditing}
              >
                <span className="ml-1 hidden sm:block">
                  {t("utils.actions.runLong", {
                    subject: t("models.reports.report_one"),
                  })}
                </span>
                <span className="ml-1 sm:hidden">{t("utils.actions.run")}</span>
              </Button>
            )}
          </div>
        )}
      </Header>
      <Page maxWidth={false}>
        <NoScroll>
          {!isLoading && !reportExists && (
            <Alert severity="warning">
              {t("views.reports.scheduled.errors.noReport")}
            </Alert>
          )}
          {(isLoading || reportExists) && (
            <div
              className={classes("flex flex-col h-full", {
                "mr-80": isEditing,
              })}
            >
              <ReportTools
                className="md:hidden"
                readOnly
                dateRange={secondsToRange(
                  viewingReport?.startDate,
                  viewingReport?.endDate
                )}
                selectedRobots={viewingReport?.robotIds}
              />
              {(() => {
                if (!report?.automateWeekly || !canReadCustomers) {
                  return <></>;
                }
                if (
                  !report.customer ||
                  report.customer.emails.length === 0 ||
                  isUndefined(report.customer.weeklyReportEnabled) ||
                  isUndefined(report.customer.weeklyReportDay) ||
                  isUndefined(report.customer.weeklyReportHour) ||
                  !report.customer.weeklyReportTimezone
                ) {
                  return (
                    <Alert severity="error" className="mb-4">
                      <AlertTitle>
                        {t("views.reports.scheduled.automation.errorTitle")}
                      </AlertTitle>
                      <ul className="list-none p-0">
                        <li>
                          {report.customer ? "✅" : "❌"}
                          <strong>
                            {t("models.customers.customer_one")}:
                          </strong>{" "}
                          {report.customer?.name ??
                            t(
                              "views.reports.scheduled.automation.reportCustomer.errors.none"
                            )}
                        </li>
                        <li>
                          {(report.customer?.emails.length ?? 0) > 0
                            ? "✅"
                            : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.reportEmails.name"
                            )}
                            :
                          </strong>{" "}
                          {(report.customer?.emails.length ?? 0) > 0
                            ? report.customer?.emails.join(", ")
                            : t(
                                "views.reports.scheduled.automation.reportEmails.errors.none"
                              )}
                        </li>
                        <li>
                          {report.customer?.weeklyReportEnabled ? "✅" : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.customerReports"
                            )}
                            :
                          </strong>{" "}
                          {report.customer?.weeklyReportEnabled
                            ? t("utils.descriptors.enabled")
                            : t("utils.descriptors.disabled")}
                        </li>
                        <li>
                          {report.customer?.weeklyReportDay ? "✅" : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.reportDay.name"
                            )}
                            :
                          </strong>{" "}
                          {report.customer?.weeklyReportDay
                            ? formatWeeklyDay(
                                t,
                                i18n,
                                report.customer.weeklyReportDay
                              )
                            : t(
                                "views.reports.scheduled.automation.reportDay.errors.none"
                              )}
                        </li>
                        <li>
                          {report.customer?.weeklyReportHour ? "✅" : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.reportHour.name"
                            )}
                            :
                          </strong>{" "}
                          {report.customer?.weeklyReportHour
                            ? formatWeeklyHour(
                                t,
                                i18n,
                                report.customer.weeklyReportHour
                              )
                            : t(
                                "views.reports.scheduled.automation.reportHour.errors.none"
                              )}
                        </li>
                        <li>
                          {report.customer?.weeklyReportTimezone ? "✅" : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.reportTimezone.name"
                            )}
                            :
                          </strong>{" "}
                          {report.customer?.weeklyReportTimezone ??
                            t(
                              "views.reports.scheduled.automation.reportTimezone.errors.none"
                            )}
                        </li>
                        <li>
                          {report.customer?.weeklyReportLookbackDays
                            ? "✅"
                            : "❌"}
                          <strong>
                            {t(
                              "views.reports.scheduled.automation.reportLookback.name"
                            )}
                            :
                          </strong>{" "}
                          {report.customer?.weeklyReportLookbackDays
                            ? `${report.customer.weeklyReportLookbackDays} ${t(
                                "utils.units.dLong",
                                {
                                  count:
                                    report.customer.weeklyReportLookbackDays,
                                }
                              )}`
                            : t(
                                "views.reports.scheduled.automation.reportLookback.errors.none"
                              )}
                        </li>
                      </ul>
                    </Alert>
                  );
                }
                return (
                  <Alert severity="warning" className="mb-4">
                    <AlertTitle>
                      {t("views.reports.scheduled.automation.warningTitle")}
                    </AlertTitle>
                    {t(
                      "views.reports.scheduled.automation.warningDescription",
                      {
                        day: formatWeeklyDay(
                          t,
                          i18n,
                          report.customer.weeklyReportDay
                        ),
                        hour: formatWeeklyHour(
                          t,
                          i18n,
                          report.customer.weeklyReportHour
                        ),
                        timezone: report.customer.weeklyReportTimezone,
                        lookback: report.customer.weeklyReportLookbackDays,
                        customer: report.customer.name,
                      }
                    )}
                  </Alert>
                );
              })()}
              <div className="flex justify-between items-center pb-4">
                <div>
                  <Typography variant="h3">
                    {(isEditing ? viewingReport : report)?.name}
                  </Typography>
                  {t("views.reports.scheduled.byline", {
                    author:
                      report?.authorName ??
                      t("views.reports.scheduled.authorUnknown"),
                  })}
                  {report?.customer
                    ? ` ${t("views.reports.scheduled.toLine", {
                        customer: report.customer.name,
                      })}`
                    : ""}
                </div>
                <div className="gap-2 items-center hidden md:flex">
                  {!isEditing && (
                    <>
                      <Button
                        variant="text"
                        className="my-4"
                        component={Link}
                        to={getReportInstancesPath(slug)}
                      >
                        {t("utils.actions.viewLong", {
                          subject: t("models.reportInstances.run_other"),
                        })}
                      </Button>
                      {(canUpdateAll || report?.authorId === user?.userId) && (
                        <Button
                          color="secondary"
                          variant="contained"
                          className="my-4"
                          onClick={() => setEditing(true)}
                        >
                          {t("utils.actions.editLong", {
                            subject: t("models.reports.report_one"),
                          })}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {isEditing && !previewReport ? (
                <Paper className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col gap-4 items-center">
                    <Typography variant="body1" color="textSecondary">
                      {t("views.reports.scheduled.previewDescription")}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() =>
                        setPreviewReport(structuredClone(viewingReport))
                      }
                    >
                      {t("views.reports.scheduled.preview")}
                    </Button>
                  </div>
                </Paper>
              ) : (
                <>
                  {isEditing && previewReport && (
                    <div className="mb-4 flex justify-end">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !report?.automateWeekly &&
                          isEqual(previewReport, viewingReport)
                        }
                        onClick={() =>
                          setPreviewReport(structuredClone(viewingReport))
                        }
                        startIcon={<RefreshIcon />}
                      >
                        {t("views.reports.scheduled.refreshPreview")}
                      </Button>
                    </div>
                  )}
                  <ReportTable
                    report={
                      (isEditing ? previewReport : viewingReport) ?? undefined
                    }
                    loading={isLoading}
                    onColumnOrderChange={
                      isEditing
                        ? ({ column, targetIndex, oldIndex }) => {
                            const { visibleColumns } = localReport;
                            const offset =
                              oldIndex - visibleColumns.indexOf(column.field);
                            visibleColumns.splice(oldIndex - offset, 1);
                            visibleColumns.splice(
                              targetIndex - offset,
                              0,
                              column.field
                            );
                            setLocalReport({
                              ...localReport,
                              visibleColumns,
                            });
                          }
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          )}
          <ReportEditor
            report={localReport}
            open={isEditing}
            onChange={setLocalReport}
            onCancel={() => {
              if (isNewReport(report)) {
                navigate(Path.REPORTS);
              } else {
                setEditing(false);
              }
            }}
            onSave={async (newReport) => {
              if (isNewReport(newReport)) {
                const createdReport = await createReport(newReport).unwrap();
                setEditing(false);
                navigate(getReportPath(createdReport.slug));
              } else {
                await updateReport(newReport);
                setEditing(false);
              }
            }}
          />
          <RunReportDialog
            report={isNewReport(report) ? undefined : report}
            open={isRunning}
            onCancel={() => setRunning(false)}
          />
        </NoScroll>
      </Page>
    </>
  );
};

export const Report = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.all
      ),
    ],
  },
  withFeatureFlag(
    {
      flag: FeatureFlag.REPORTS,
      noFlag: "/",
    },
    _Report
  )
);
